import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/top-5-last-minute-summer-getaways`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="Top 5 Last Minute Summer Trips | Win Epic Prizes | DAYMADE"
        description="It’s time to get your travelling mojo back. Discover the top 5 last-minute summer getaways. Enter now for a chance to win your dream holiday. "
        openGraph={{
          title: 'Top 5 Last Minute Summer Trips | Win Epic Prizes | DAYMADE',
          description:
            'It’s time to get your travelling mojo back. Discover the top 5 last-minute summer getaways. Enter now for a chance to win your dream holiday.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/top-5-last-minute-summer-getaways',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'A landscape view of the river and Charles Bridge in Prague',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'winner,lottery,daymade,jackpot,raffle,holiday competitions,win prizes,win a prize, win a free trip,Sintra holiday,Northern Ireland holiday,Prague holiday,Paris holiday,Stockholm holiday,last-minute holiday ideas,all expenses paid trip,win a trip,win a luxury holiday,win gift cards,holiday prizes to win',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="A landscape view of the river and Charles Bridge in Prague"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            Top 5 Last-Minute Summer Getaways
          </h1>

          <p className="subheading">
            Don’t miss out on your ultimate summer vacay.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            Are you seeing all your pals enjoying travelling again and kicking
            yourself for not booking any fun trips? We hear you! We’ve got the
            perfect cure to fix that travel envy. Let’s make your summer the
            best one yet. Our fave last-minute breaks are jam-packed with
            culture and fantastic food, and you can even sleep in a quirky
            bubble dome. To top it all off, you can win a free holiday from
            DAYMADE. That’s right, you’ll be smug as anything and make all your
            pals jealous. Plus, your Insta stories will be popping! Here are the
            best last-minute getaways to have an unforgettable summer:
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="A suitcase opened with holiday clothes, a sun hat, a camera, and toiletries. "
            className="singleBlogImage"
          />

          <h2>Prague</h2>

          <p>
            Prague is bursting with history, art and castles that will make you
            feel like you're living in a fairytale. Plus, it's the bohemian-beer
            capital of the world. Prague ticks all the boxes for a perfect
            summer getaway. Dive into a brand new culture and hang out in some
            of the best beer gardens, even better than your local (we promise!).
            You can even have a beer bath if you love it that much. Beer ain't
            your thing? Explore the gothic capital and immerse yourself in the
            Czech galleries, museums and fantasy street art. Even better, you
            can{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/win-holiday-to-prague"
              target="_blank"
              rel="noreferrer"
            >
              win our long break to Prague
            </a>
            , which is an all-expense paid trip! That’s right, your return
            flights and 5 nights at the luxury Falkensteiner hotel are all
            covered by us. We’ve even thrown in a river cruise lunch to enjoy a
            relaxing afternoon. Just make sure you treat yourself to some spa
            waffles and bohemian keepsakes.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A city landscape view of Prague, boats on the river, and the cathedral."
            className="singleBlogImage"
          />

          <h2>Northern Ireland</h2>

          <p>
            Fancy a flight-free trip, so you’re travelling sustainably this
            summer? It's time to explore magical Northern Ireland and all its
            hidden treasures. Get stuck in and learn about all the myths and
            legends of the Giant's Causeway. The perfect time to visit is in the
            summer when the views are spectacular and it's not too cold from
            those sea winds. Are there any Game of Throne fans in the house?
            Then you must head to the Dark Hedges to relive Arya Stark’s journey
            on the Kingsroad. Those mystical trees are just something you've
            never seen before. Wanna have a unique experience and sleep in a
            bubble dome with your fellow traveller? Hell yeah!{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/northern-ireland-2000"
              target="_blank"
              rel="noreferrer"
            >
              Win a trip to Northern Ireland
            </a>{' '}
            for 5 nights to stay in your very own bubble dome surrounded by
            charming Irish forests.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="A bubble dome in the forest and a woman looking down from a hill wearing a yellow raincoat and rain boots."
            className="singleBlogImage"
          />

          <h2>Stockholm</h2>

          <p>
            We can't get enough of the Scandinavian lifestyle, and we're all
            obsessed with all things minimalist. Why not experience the real
            thing and head over to Stockholm? The vibrant city is filled with
            sustainable dining, world-class art and the charming old town of
            Gamla Stan. Of course, eating Swedish meatballs is on top of our
            bucket list (no need to visit Ikea to get your fix!). Stockholm has
            so much to offer, and we're dying for you to go. That's why we've
            teamed up with LuckyTrip so you can experience Stockholm in style!
            You could win a{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/stockholm-luckytrip-2000"
              target="_blank"
              rel="noreferrer"
            >
              luxury holiday
            </a>{' '}
            with your fellow Scandi fan and you’ll also win tickets for the
            iconic Fotografiska museum. Experience the best photography
            exhibition that will make you feel inspired and spark that
            creativity!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="A city landscape view of Stockholm and the river."
            className="singleBlogImage"
          />

          <h2>Sintra</h2>

          <p>
            Lisbon is vibrant and filled with life, but fancy going off the
            beaten track this summer? We've got just the gem for you! Head over
            to the low-key and calming Sintra this summer. The picturesque town
            makes you feel like you're living in a postcard. We're talking
            castles (lots of amazing ones!), enchanted gardens and beaches with
            some of the best waves in the world. Don't worry though, as the
            beaches match the town's tranquillity. It's a popular summer spot
            for Português, so you'll get an authentic, local experience. Fun
            fact: Nazaré, to the north of Sintra, is where the world's tallest
            wave is, yikes! Wanna enjoy the slow-paced life the town offers? How
            about winning{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/win-holiday-sintra"
              target="_blank"
              rel="noreferrer"
            >
              5 nights in Sintra
            </a>
            , so you're not rushing up and down those castles? We've got you!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="A landscape view of the Pena Palace of Sintra on top of the mountain."
            className="singleBlogImage"
          />

          <h2>Paris</h2>

          <p>
            We've got another trip for you sustainable travel fans. Escape to
            the romantic capital of the world: Paris! You can pop over for the
            weekend with no flying involve “cheers, Eurostar!”. Some of the best
            things about the glam French capital are the coffee, croissants,
            macaroons, baguettes… okay, you see where we’re going with this! If
            there’s anything you need to go to Paris for, it’s to wine and dine
            your heart out. You can even have some food to go and have it on the
            steps with the perfect view of the iconic Eiffel Tower (no
            reservation required). Discover some hidden gems in the hipster area
            of Canal Saint Martin and get lost in the endless pretty streets.
            You can even visit the famous Shakespeare & Co bookstore for some
            new reads during your relaxing morning at the cafes. Of course,
            don’t forget to pay Mona a visit whilst you’re there!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6.jpg`}
            alt="A woman wearing a sun hat looking at the Eiffel Tower from a bridge and pointing towards it."
            className="singleBlogImage"
          />

          <p>
            Are you getting the travel bug? We have tons of epic experiences and
            holiday prizes to win. Feeling inspired but fancy somewhere else?
            You can win gift cards to book your dream trip! Check out our{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/airbnb-2022"
              target="_blank"
              rel="noreferrer"
            >
              Airbnb getaway gift card
            </a>
            , where you'll be spoilt for choice on unique stays! We want you to
            get your travelling mojo back and make your day! So what are you
            waiting for?
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary7.jpg`}
            alt="A bright bedroom with a fireplace and glass doors surrounded by nature."
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            Do you want to be in with a chance of winning your dream summer
            break?
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
